import React, { useState } from 'react';

import TranscriptVideoPlayer from './TranscriptVideoPlayer';
import { MagnifierPreview } from './VideoMagnifier';

import type {
  ExternalPlayerControls,
  VideoTimeRange,
} from './TranscriptVideoPlayer';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

/**
 * `TranscriptVideoPlayer` plus its magnifier host wiring as a single unit.
 *
 * The player only *reports* magnifier state (`onMagnifierUpdate` /
 * `onVideoElementReady`); the zoomed block itself is a separate
 * `MagnifierPreview` the host must render and feed. A host that mounts the
 * bare player without that wiring gets a zoom toggle that silently does
 * nothing (PLAT-10261 review finding). This composite owns the wiring so a
 * single mount yields a working magnifier, with the preview pinned directly
 * above the player. `MagnifierPreview` renders null while the magnifier is
 * inactive, so there is no layout cost when idle.
 */

interface TranscriptPlayerWithMagnifierProps {
  videoCreator: ITranscriptVideoCreator;
  zoomLevel: number;
  /** Optional story-absolute playback bound — threaded to the player (PLAT-10286). */
  timeRange?: VideoTimeRange;
  /** Highlight-only playback windows — threaded to the player (PLAT-10949). */
  playbackSegments?: VideoTimeRange[];
  /** Open parked at the shared playhead — threaded to the player (PLAT-10286). */
  anchorToCreatorTimeOnLoad?: boolean;
  /** Loading-placeholder poster (clip thumbnail) — threaded to the player (PLAT-10286). */
  poster?: string;
  /** Mute the video's own audio — set in Dubbed mode so only the dub plays (PLAT-10597). */
  muted?: boolean;
  /** The video's own audio as a share of the bar's volume, 0–1 (PLAT-11927). */
  audioLevel?: number;
  /** Session-local upload URL, mp4 fallback while renditions pend (PLAT-11228). */
  localPreviewUrl?: string | null;
  /** The host renders the controls; the player's own stand down (PLAT-11657). */
  externalControls?: boolean;
  /** The verbs for a host's transport bar — threaded to the player (PLAT-11657). */
  onControls?: (controls: ExternalPlayerControls | null) => void;
  /** No magnifier toggle over the picture — threaded to the player (PLAT-11683). */
  hideMagnifier?: boolean;
}

export const TranscriptPlayerWithMagnifier: React.FC<
  TranscriptPlayerWithMagnifierProps
> = ({
  videoCreator,
  zoomLevel,
  timeRange,
  playbackSegments,
  anchorToCreatorTimeOnLoad,
  poster,
  muted,
  audioLevel,
  localPreviewUrl,
  externalControls,
  onControls,
  hideMagnifier,
}) => {
  const [magnifierMousePos, setMagnifierMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [magnifierVideoEl, setMagnifierVideoEl] =
    useState<HTMLVideoElement | null>(null);

  return (
    <>
      <MagnifierPreview
        videoElement={magnifierVideoEl}
        mousePos={magnifierMousePos}
      />
      <TranscriptVideoPlayer
        videoCreator={videoCreator}
        zoomLevel={zoomLevel}
        timeRange={timeRange}
        playbackSegments={playbackSegments}
        anchorToCreatorTimeOnLoad={anchorToCreatorTimeOnLoad}
        poster={poster}
        muted={muted}
        audioLevel={audioLevel}
        localPreviewUrl={localPreviewUrl}
        externalControls={externalControls}
        onControls={onControls}
        hideMagnifier={hideMagnifier}
        onMagnifierUpdate={setMagnifierMousePos}
        onVideoElementReady={setMagnifierVideoEl}
      />
    </>
  );
};
